import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { accessorialsService } from '../../../services/accessorials.service';

/**
 * Delete Accessorial — the port of V1's `chkdelete` prompt for `accessorialform`
 * (main.pl:8379). V1 refuses to submit on an empty box ("Reason for Delete must be
 * filled out"), and the reason is the whole point: it is written to history as the
 * 'Accessorial Deleted' row, dropped on the pro as a note, and mailed to the CSR.
 *
 * The copy says what deleting actually does, which differs from Cancel Invoice in
 * both directions — nothing goes back to a billable state, and nothing is destroyed
 * either. The row is soft-deleted and drops out of the list permanently (there is no
 * undelete), while its line items stay behind and its number is never reused.
 */
const DeleteAccessorialDialog: React.FC<{
  pronumber: number | string;
  recno: number;
  accessorialNum: number;
  onClose: () => void;
  onDeleted: () => void;
}> = ({ pronumber, recno, accessorialNum, onClose, onDeleted }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // A ref, not the `busy` state: two clicks in the same tick both read the pre-render
  // value of state and both submit. The second call is refused server-side ("already
  // been deleted"), but only after the first has written history, a note and an email
  // — so the user would see an error for a delete that in fact succeeded.
  const submitting = useRef(false);
  const trimmed = reason.trim();

  const submit = async () => {
    if (!trimmed || submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      await accessorialsService.remove(pronumber, recno, trimmed);
      onDeleted();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not delete the accessorial.');
      submitting.current = false;
      setBusy(false);
    }
  };

  // `stopPropagation` is load-bearing, not boilerplate. This dialog is opened from
  // the accessorial editor, whose backdrop carries onClick={onClose}, and React
  // bubbles synthetic events up the COMPONENT tree rather than the DOM one — so
  // portaling out of that subtree does not escape the handler, and clicking into the
  // textarea below dismissed the whole editor along with the half-typed reason. The
  // editor's own panel stops its clicks the same way.
  //
  // The portal is still worth having for stacking: z-[140] over the editor's z-[130],
  // with no ancestor stacking context to fight.
  return createPortal(
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center bg-scrim/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Delete accessorial"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="w-full max-w-lg overflow-hidden rounded-lg border border-rule bg-panel shadow-xl">
        <div className="border-b border-hairline px-4 py-3">
          <h2 className="text-base font-bold text-heading">
            Delete accessorial {accessorialNum}
          </h2>
        </div>
        <div className="space-y-3 px-4 py-4 text-sm text-body">
          <p>
            It is removed from this order&apos;s accessorials and cannot be restored. Its
            line items are kept, and accessorial number {accessorialNum} is not reused.
          </p>
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted">
              Reason for deleting (required)
            </span>
            <textarea
              autoFocus
              rows={5}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={2000}
              className="w-full rounded border border-rule bg-input px-2 py-1.5 text-sm text-body"
              placeholder="Why is this accessorial being deleted?"
            />
          </label>
          <p className="text-xs text-muted">
            Saved to history and as a note on the pro, and emailed to the order&apos;s CSR.
          </p>
          {error && <p role="alert" className="text-sm text-danger-fg">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-hairline bg-sunken px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-2 text-sm text-label underline disabled:opacity-50"
          >
            Keep accessorial
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!trimmed || busy}
            className="rounded-md bg-brand-red px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Deleting…' : 'Delete accessorial'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DeleteAccessorialDialog;
